/**
 * Input — Business app form input primitive.
 *
 * Enforces UX Blueprint §9:
 *   - min-height 56px (tablet-first touch target)
 *   - Visible focus ring
 *   - Pairs with <Label>, <FieldError> for a11y
 */
import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...rest }, ref) => {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full min-h-biz-touch px-biz-content-lg py-biz-content',
          'bg-biz-background text-biz-foreground',
          'border border-biz-input rounded-biz-input',
          'placeholder:text-biz-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:border-biz-primary',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors duration-biz-fast',
          invalid && 'border-biz-danger focus-visible:ring-biz-danger',
          className,
        )}
        {...rest}
      />
    );
  },
);
Input.displayName = 'Input';
